import { CloudArrowUpIcon } from '@heroicons/react/24/outline';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type ChangeEvent, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import Button from '@/components/UI/Button';
import Modal from '@/components/UI/Modal';
import { type SaveProjectThumbnailPayload, saveProjectThumbnail } from '../../../../../../../api/project';
import { SherpaProjectReactQueryKey } from '../../../../../../../constants/ReactQueryKeys';
import useUpload from '../../../../../../../hooks/useUpload';
import { notifyUser } from '../../../../../../../store/Toast';
import { sanitizeResponseError } from '../../../../../../../utils';

interface IChangeProjectThumbnailProps {
  handleModal: () => void;
}

const ChangeProjectThumbnail = ({ handleModal }: IChangeProjectThumbnailProps) => {
  const { projectId } = useParams<string>();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [imagePreviewUrl, setImagePreviewUrl] = useState('');
  const [file, setFile] = useState<any>('');
  const [errors, setErrors] = useState({
    picture: '',
  });
  const thumbnailUpload = useUpload();

  const postProjectThumbnail = useMutation(
    (data: SaveProjectThumbnailPayload) => saveProjectThumbnail({ projectId: parseInt(projectId!, 10), data }),
    {
      onSuccess: () => {
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: 'Project Thumbnail upload successful',
            toastType: 'success',
          })
        );
        modalHandler();
        queryClient.invalidateQueries(['get-projects']);
        queryClient.invalidateQueries([SherpaProjectReactQueryKey.GET_PROJECT, undefined, parseInt(projectId!, 10)]);
      },
      onError: (error: any) => {
        const resError = sanitizeResponseError(error);
        dispatch(
          notifyUser({
            showToast: true,
            toastMessage: resError,
            toastType: 'failed',
          })
        );
      },
    }
  );

  const fileChangeHandler = async (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const file = e.target.files?.[0];
    if (file) {
      setErrors((prevState) => {
        return { ...prevState, picture: '' };
      });
      setFile(file);
      setImagePreviewUrl(URL.createObjectURL(file));
    }
  };

  const saveProjectThumbnailHandler = (data: { key: string; type: string; size: number }) => {
    postProjectThumbnail.mutate({
      name: file.name,
      key: data.key,
      contentType: data.type,
      size: data.size,
    });
  };

  const imageUploadHandler = () => {
    if (!file) {
      setErrors((prevState) => {
        return {
          ...prevState,
          picture: 'Picture is required to upload',
        };
      });
    } else {
      thumbnailUpload.uploadFile({
        file,
        resource: 'projects',
        resourceId: parseInt(projectId!, 10),
        path: 'thumbnails',
        dispatchNotification: false,
        applyData: saveProjectThumbnailHandler,
      });
    }
  };

  const modalHandler = () => {
    setImagePreviewUrl('');
    setFile('');
    setErrors((prevState) => {
      return { ...prevState, picture: '' };
    });
    handleModal();
  };

  return (
    <Modal customStyle='xl:w-1/5'>
      <div className=''>
        <h2 className='mt-0'>Upload Project Thumbnail</h2>
        {imagePreviewUrl && (
          <div className='flex justify-center items-center'>
            <img src={imagePreviewUrl} className='rounded-md h-72 w-72 object-fit' alt='preview' />
          </div>
        )}
        <span className='relative flex flex-col mt-5'>
          <label htmlFor='projectThumbnailImage' className='flex flex-col justify-center items-center'>
            {!file && <CloudArrowUpIcon className='h-10 w-10 text-primaryColor-normal' />}
            <span className='underline hover:text-primaryColor-normal cursor-pointer'>{file ? 'Change' : 'Choose'} Picture</span>
          </label>
          <input
            type='file'
            id='projectThumbnailImage'
            name='projectImage'
            className='h-full cursor-pointer hidden'
            accept='.jpg, .jpeg, .png'
            onChange={fileChangeHandler}
            disabled={postProjectThumbnail.isLoading}
          />
        </span>
        {errors.picture && <p className='mt-2 text-tertiaryColor-normal text-center'>{errors.picture}</p>}
        <div className='flex  justify-end gap-4 mt-8'>
          <Button label='Cancel' btnStyle='btn-neutral' type='button' onClick={modalHandler} />
          <Button
            label={`${postProjectThumbnail.isLoading || thumbnailUpload.isUploading ? 'Uploading...' : 'Upload'}`}
            type='button'
            btnStyle='btn-primary'
            onClick={imageUploadHandler}
            disabled={postProjectThumbnail.isLoading || thumbnailUpload.isUploading}
          />
        </div>
      </div>
    </Modal>
  );
};

export default ChangeProjectThumbnail;
